import {
  LatitudeLongitude,
  SubjectData,
  noop,
  orUndefined,
  schAloftPrice,
} from '@aloft/toolkit-shared';

import { useEffect, useState } from 'react';

import { CompAdjustments } from '../../../redux/features/selectors/CompAdjustments.ts';
import { selectManualAdjustmentsEntities } from '../../../redux/features/selectors/propertyDataSelectors/manualAdjustmentsSelectors.ts';
import {
  CompPropertyDataState,
  PropertyDataState,
  ReconciliationState,
} from '../../../redux/types.ts';
import { SalesComparisonGridColumnData } from '../../tkv2PDF/types.ts';
import { convertSalesComparisonDataToPDFGridColumn } from '../../tkv2PDF/utils/convertSalesComparisonDataToPDFGridColumn.ts';

/**
 * Custom hook to transform sales comparison data for PDF/API export
 */
export const useTransformSalesComparisonData = (
  subject: SubjectData | undefined,
  compSequence: string[],
  propertyDataEntities: Record<string, PropertyDataState>,
  adjustments: Record<string, CompAdjustments>,
  manualAdjustmentsEntities: ReturnType<typeof selectManualAdjustmentsEntities>,
  coordinates?: LatitudeLongitude,
  entityListingPhotos?: Record<string, Blob[]>,
  effectiveYear?: number,
  hasArvOption?: boolean,
  reconciliation?: ReconciliationState,
) => {
  const [subjectSalesComparisonData, setSubjectSalesComparisonData] =
    useState<SalesComparisonGridColumnData | null>(null);

  const [subjectArvSalesComparisonData, setSubjectArvSalesComparisonData] =
    useState<SalesComparisonGridColumnData | null>(null);

  const [comparablesSalesComparisonData, setComparablesSalesComparisonData] = useState<
    Record<string, SalesComparisonGridColumnData>
  >({});

  // Transform subject data
  useEffect(() => {
    if (subject) {
      const subjectAdjustments = {
        totalCostToCure: {
          adjustment: orUndefined(schAloftPrice.parse)(reconciliation?.totalCostToCure),
        },
      } as CompAdjustments;

      convertSalesComparisonDataToPDFGridColumn(
        'subject',
        compSequence,
        subject,
        subjectAdjustments,
        undefined,
        undefined,
        entityListingPhotos?.subject,
        effectiveYear,
      )
        .then(setSubjectSalesComparisonData)
        .catch(console.error);

      if (hasArvOption) {
        convertSalesComparisonDataToPDFGridColumn(
          'subjectArv',
          compSequence,
          subject,
          undefined,
          undefined,
          undefined,
          entityListingPhotos?.subject,
          effectiveYear,
        )
          .then(setSubjectArvSalesComparisonData)
          .catch(console.error);
      }
    }
  }, [subject, entityListingPhotos, compSequence, effectiveYear, hasArvOption, reconciliation]);

  // Transform comparables data
  useEffect(() => {
    const loadComparablesData = async () => {
      const compPromises = compSequence
        .filter((compId) => propertyDataEntities[compId] && adjustments[compId])
        .map(async (compId) => {
          const comp = await convertSalesComparisonDataToPDFGridColumn(
            compId,
            compSequence,
            (propertyDataEntities[compId] as CompPropertyDataState).compData,
            adjustments[compId],
            manualAdjustmentsEntities[compId],
            coordinates,
            entityListingPhotos?.[compId],
            effectiveYear,
          );
          return comp !== null ? ([compId, comp] as const) : null;
        });

      const results = await Promise.all(compPromises);
      const newData: Record<string, SalesComparisonGridColumnData> = Object.fromEntries(
        results.filter(
          (result): result is [string, SalesComparisonGridColumnData] => result !== null,
        ),
      );

      setComparablesSalesComparisonData(newData);
    };

    if (compSequence.length > 0) loadComparablesData().then(noop).catch(console.error);
  }, [
    compSequence,
    propertyDataEntities,
    adjustments,
    coordinates,
    entityListingPhotos,
    manualAdjustmentsEntities,
    effectiveYear,
  ]);

  return {
    subjectSalesComparisonData,
    subjectArvSalesComparisonData,
    comparablesSalesComparisonData,
  };
};
